import { useCallback } from 'react';
import { useCartUI } from '@context/CartContext';
import type { Cart, ItemBody } from '../../types/cart';
import { HookError } from '../../errorHandlers';
import { removeItemFetcher } from './useRemoveItem';

interface ItemBodyRequiredQuantity extends ItemBody {
  quantity: number;
}

export const updateItemFetcher = async (
  itemId: string,
  itemBody: ItemBodyRequiredQuantity
): Promise<Cart> => {
  if (!itemId) {
    throw new HookError('ItemId must be provided to update item in cart');
  }

  if (!Number.isInteger(itemBody.quantity)) {
    throw new HookError(
      'Quantity must be provided as an integer to update item in cart'
    );
  }

  if (itemBody.quantity < 1) return removeItemFetcher(itemId);

  const updateItemResponse = await fetch('/api/cart', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ itemId, item: itemBody }),
  });

  const { data } = await updateItemResponse.json();
  return data;
};

const useUpdateItem = (onUpdateItem?: ({ data }: { data: Cart }) => void) => {
  const { updateCart } = useCartUI();
  return useCallback(
    async (itemId: string, itemBody: ItemBodyRequiredQuantity) => {
      const data = await updateItemFetcher(itemId, itemBody);
      updateCart(data);
      try {
        onUpdateItem?.({ data });
      } catch (error) {
        console.log(error);
      }
    },
    [onUpdateItem, updateCart]
  );
};

export default useUpdateItem;
